import PropTypes from 'prop-types';
// Components
import { Button } from '@repo/zolintra-ui';
// Utils
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// Services
import { deletePost } from '../../../services/post/postService';
// Hooks
import useToast from '../../../hooks/useToast';
import useResources from '../../../hooks/domain/useResources';
import { useDialog } from '../../../hooks/useDialog';
import { useResourcesListQuery } from '../../../services/resource/resourceQueries';
import usePost from '../../../hooks/domain/usePost';
import { useAppContext } from '../../../store/AppContext';
// Permissions
import hasPermission, { PERMISSIONS } from '../../../permissions/hasPermission';

import styles from './DeleteConfirmDialog.module.css';

function DeleteConfirmDialog({ request, year }) {
  const { t } = useTranslation();
  const [{ user }] = useAppContext();

  const queryClient = useQueryClient();

  const hasDeleteAnyAndResourcePermission = (
    hasPermission(PERMISSIONS.GET_ANY_RESOURCE, user.prm)
    && hasPermission(PERMISSIONS.DELETE_ANY_POST, user.prm)
    && hasPermission(PERMISSIONS.IDENTITY_ACCESS_GET_ANY_RESOURCE, user.prm)
  );

  // Hooks
  const toast = useToast();
  const { setDialog } = useDialog();
  const { getResourceDisplayName } = useResources();
  const resourcesListQuery = useResourcesListQuery({
    enabled: hasDeleteAnyAndResourcePermission,
  });
  const { displayDateFromSlotId } = usePost();

  // Mutation
  const { mutate, isPending } = useMutation({
    mutationFn: deletePost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['postsList'] });
      setDialog({ type: 'HIDE_DIALOG' });
      toast.notify({
        type: 'success',
        message: t('remoteWorkingAndLeave.listSubView.table.deleteSuccess'),
      });
    },
  });

  return (
    <div>
      {hasDeleteAnyAndResourcePermission ? t(
        'remoteWorkingAndLeave.deleteDialog.text',
        {
          user: getResourceDisplayName(
            resourcesListQuery.data?.find((res) => res.id === request.resourceId),
          ),
          startDate: displayDateFromSlotId({
            slotId: request.includedStartSlotId,
            year,
            locale: 'fr',
          }),
        },
      ) : t('remoteWorkingAndLeave.deleteDialog.textOwn', {
        startDate: displayDateFromSlotId({
          slotId: request.includedStartSlotId,
          year,
          locale: 'fr',
        }),
      })}

      <div className={styles.buttons}>
        <Button
          variant="bordered"
          onClick={() => setDialog({ type: 'HIDE_DIALOG' })}
        >
          {t('remoteWorkingAndLeave.deleteDialog.cancelAction')}
        </Button>
        <Button
          onClick={() => mutate(
            { year, postId: request.id },
          )}
          isLoading={isPending}
        >
          {t('remoteWorkingAndLeave.deleteDialog.confirmAction')}
        </Button>
      </div>
    </div>
  );
}

DeleteConfirmDialog.propTypes = {
  request: PropTypes.shape({
    id: PropTypes.string,
    resourceId: PropTypes.string,
    includedStartSlotId: PropTypes.number,
  }).isRequired,
  year: PropTypes.string.isRequired,
};

export default DeleteConfirmDialog;
